import type { DevicesDevice, DevicesDeviceState } from '@smarthome/api';

import { getDeviceOn } from '../../utils';

import { Toggle } from '@/components/ui';
import { cn } from '@/components/ui/utils';

export interface SwitchControlsProps {
  device: DevicesDevice;
  onUpdateState: (state: DevicesDeviceState) => void;
  disabled?: boolean;
}

export function SwitchControls({ device, onUpdateState, disabled }: SwitchControlsProps) {
  const capabilities = device.capabilities?.switch;
  const switchState = device.state?.switch;
  const isOn = getDeviceOn(device);

  const positions = capabilities?.positions ?? [];
  const isMultiPosition = positions.length > 0;
  const currentPosition = switchState?.position;
  const isMomentary = capabilities?.momentary ?? false;

  const handlePowerToggle = (checked: boolean) => {
    onUpdateState({ switch: { on: checked } });
  };

  const handlePositionSelect = (positionId: string) => {
    onUpdateState({ switch: { position: positionId } });
  };

  // For momentary switches, we show a pulse button instead of a toggle
  if (isMomentary && !isMultiPosition) {
    return (
      <div className="space-y-1">
        <div className="rounded-lg px-3 py-2.5">
          <button
            type="button"
            onClick={() => onUpdateState({ switch: { on: true } })}
            disabled={disabled}
            className={cn(
              'w-full rounded-lg border-2 border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium transition-all',
              'hover:border-blue-300 hover:bg-blue-50 active:scale-[0.98] active:bg-blue-100',
              'dark:border-white/[0.08] dark:bg-zinc-800/50 dark:hover:border-blue-500/50 dark:hover:bg-blue-500/10 dark:active:bg-blue-500/20',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            Pulse
          </button>
          <p className="mt-2 text-center text-xs text-gray-500 dark:text-zinc-500">
            Momentary switch - press to activate
          </p>
        </div>
      </div>
    );
  }

  // Multi-position switch: show buttons for each position
  if (isMultiPosition) {
    return (
      <div className="space-y-1">
        <div className="px-3 py-2">
          <p className="mb-3 text-sm font-medium text-gray-700 dark:text-zinc-300">Position</p>
          <div className="flex flex-wrap gap-2">
            {positions.map((position) => {
              const isSelected = currentPosition === position.id;
              return (
                <button
                  key={position.id}
                  type="button"
                  onClick={() => handlePositionSelect(position.id!)}
                  disabled={disabled}
                  className={cn(
                    'rounded-lg border-2 px-4 py-2 text-sm font-medium transition-all',
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-500/20 dark:text-blue-300'
                      : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300 hover:bg-gray-100 dark:border-white/[0.08] dark:bg-zinc-800/50 dark:text-zinc-300 dark:hover:border-white/[0.12] dark:hover:bg-zinc-800',
                    disabled && 'cursor-not-allowed opacity-50'
                  )}
                >
                  {position.label || position.id}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Standard on/off switch
  return (
    <div className="space-y-1">
      <div className="rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.02]">
        <Toggle
          label="Power"
          layout="switch-right"
          checked={isOn ?? false}
          onChange={handlePowerToggle}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
